import { inject, Injectable, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, from, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { ModalService } from '../modals/modal.service';
import { environment } from '../../../environments/environment';
import { SIGN_IN_MODAL_NAME, SignInComponent } from '../../components/authentication/sign-in/sign-in.component';
import { TicketService } from '../tickets/ticket.service';
import { EmbeddedPaymentService } from '../epf/embedded-payment.service';
import { IAccountData } from '../../models/IAccountData';
import { CommonHttpService } from '../common-http.service';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { ICreditCardRequest } from '../../models/ICreditCardRequest';
import { RegistrationSuccessfulComponent } from '../../components/authentication/registration-successful/registration-successful.component';
import { ICreditCardResponse } from '../../models/ICreditCardResponse';
import { ForgotPasswordComponent } from '../../components/authentication/forgot-password/forgot-password.component';
import { FORGOT_PASSWORD_FAILURE } from '../../components/authentication/forgot-password/callback/forgot-password-failure-callback.component';
import { FORGOT_PASSWORD } from '../../components/authentication/forgot-password/callback/forgot-password-callback.component';
import { LOGGED_IN } from '../../components/authentication/oidc/callback/oidc-callback.component';
import { PASSWORD_RESET_RESULTS_MODAL_NAME, PasswordResetResultsComponent } from '../../components/authentication/forgot-password/results/password-reset-results/password-reset-results.component';
import { IPasswordRequest } from '../../models/IPasswordRequest';
import { GO_TO_LOGIN } from '../../components/authentication/sign-in/callback/show-login-callback.component';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { OIDC_ERROR_UNAUTHORIZED_MODAL_NAME, OidcErrorUnauthorizedComponent } from '../../components/authentication/oidc/error/oidc-error-unauthorized.component';
import { ERROR_UNAUTHORIZED } from '../../components/authentication/oidc/callback/oidc-error-unauthorized-callback.component';


export const LOGIN_TYPE_TOPBAR = "topbar";
export const LOGIN_TYPE_TICKETS = "tickets";
export const LOGIN_TYPE_BID_INLINE = "bid_inline";
export const LOGIN_TYPE_REGISTER = "register";
export const LOGIN_TYPE_PASSWORD_RESET = "password_reset";
export const LOGIN_TYPE_COMPLETE_REGISTRATION = "complete_registration";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private modalService = inject(ModalService);
  private ticketService = inject(TicketService);
  private paymentService = inject(EmbeddedPaymentService);
  private httpService = inject(CommonHttpService);
  private reCaptchaV3Service = inject(ReCaptchaV3Service);

  errorSignal = signal<string>('');

  private loginEventCallbacks = new Set<(event: MessageEvent) => void>();
  isAuthenticated$: Observable<boolean>;
  userData$: Observable<any>;
  errorMessage: string | null = null;

  registerResponseSignal = signal<string | null>(null);
  creditCardResponseSignal = signal<any | null>(null);
  creditCardSignal = signal<ICreditCardResponse | null>(null);
  constructor(private oidcSecurityService: OidcSecurityService, private http: HttpClient) {
    this.isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
      map(auth => auth.isAuthenticated)
    );
    this.userData$ = this.oidcSecurityService.userData$;
    // console.log('Is Auth:' + this.isAuthenticated$);

  }

  public openLoginModal(
    loginPage: string = LOGIN_TYPE_TOPBAR,

    postLoginCallback: () => void = () => { },
    auctionId: number = 0,
    alternativeCallback: () => void = () => { },
    passwordResetToken = "",
    options?: { hideSuccessButton?: boolean; hideGuestOption?: boolean }

  ) {
    if (loginPage == null || loginPage == undefined) {
      loginPage = LOGIN_TYPE_TOPBAR;
    }
    let customParams = {};
    if (loginPage === LOGIN_TYPE_REGISTER) {
      customParams = {
        prompt: "create",
        auctionId: auctionId,
      }
    } else if (loginPage === LOGIN_TYPE_PASSWORD_RESET) {
      customParams = { isPasswordReset: true, bfgToken: passwordResetToken }
    } else if (loginPage === LOGIN_TYPE_COMPLETE_REGISTRATION) {
      customParams = {
        prompt: "create",
        bfgToken: passwordResetToken,
        auctionId: auctionId,
      }
    }
    let urlHandler = (url: string) => {
      this.modalService.openModal(SIGN_IN_MODAL_NAME, SignInComponent, {
        inputValues: {
          loginUrl: url,
          loginPage: loginPage,
          alternativeCallback: alternativeCallback,
          auctionId: auctionId,
          hideGuestOption: options?.hideGuestOption ?? false
        },
        modal: true,
        dismissableMask: true,
        closable: true,
        breakpoints: {
          '640px': '450px',
          '450px': '100%'
        }
      });
    }
    let messageCallback = () => {
      this.modalService.closeModal(SIGN_IN_MODAL_NAME);
      postLoginCallback();
    };
    if (loginPage === LOGIN_TYPE_REGISTER) {
      messageCallback = () => {
        this.modalService.closeModal(SIGN_IN_MODAL_NAME);
        this.modalService.openModal(
            'registration-successful',
            RegistrationSuccessfulComponent,
            {
              inputValues: {
                hideButton: options?.hideSuccessButton 
              }
            }
        );
        postLoginCallback();
      }
    }
    if (loginPage === LOGIN_TYPE_COMPLETE_REGISTRATION) {
      messageCallback = () => {
        this.modalService.closeModal(SIGN_IN_MODAL_NAME);
        this.modalService.openModal(
            'registration-successful',
            RegistrationSuccessfulComponent,
            {
              inputValues: {
                hideButton: options?.hideSuccessButton
              }
            }
        );
        postLoginCallback();
      }
    }
    if (loginPage === LOGIN_TYPE_PASSWORD_RESET) {
      messageCallback = () => {
        this.modalService.closeModal(SIGN_IN_MODAL_NAME);
        this.modalService.openModal(PASSWORD_RESET_RESULTS_MODAL_NAME, PasswordResetResultsComponent, {
          inputValues: {
            success: true
          },
          modal: true,
          dismissableMask: true,
          closable: true,
        });
        postLoginCallback();
      }
    }
    this.login(urlHandler, messageCallback, customParams, auctionId);
  }

  continueAsAGuest(continueAsGuestCallback: () => void) {
    this.modalService.closeModal(SIGN_IN_MODAL_NAME);
    continueAsGuestCallback();
  }

  public login(
    urlHandler: (url: string) => void,
    postLoginCallback: () => void,
    customParams = {},
    auctionId: number
  ) {
    let authOptions = { urlHandler, customParams: customParams };
    this.loginEventCallbacks.forEach(callback => window.removeEventListener("message", callback));
    this.loginEventCallbacks.clear();
    let eventCallback = (event: MessageEvent) => {
      if (event.origin !== window.origin || event.data !== LOGGED_IN) {
        return
      };
      window.removeEventListener("message", eventCallback);
      this.loginEventCallbacks.delete(eventCallback);
      postLoginCallback();
      this.oidcSecurityService.checkAuth().subscribe(({ accessToken }) => {
        if (!environment.production) {
          console.log("Access token: " + accessToken);
        }
      });
    }
    this.loginEventCallbacks.add(eventCallback);
    window.addEventListener(
      "message",
      eventCallback
    );
    let forgottenPasswordCallback = (event: MessageEvent) => {
      if (event.origin !== window.origin || event.data !== FORGOT_PASSWORD) {
        return
      };
      window.removeEventListener("message", forgottenPasswordCallback);
      this.loginEventCallbacks.delete(forgottenPasswordCallback);
      this.modalService.closeModal(SIGN_IN_MODAL_NAME);
      this.modalService.openModal('app-forgot-password', ForgotPasswordComponent, {
        inputValues: {
          auctionId: auctionId
        },
        modal: true,
        dismissableMask: true,
        closable: true,
      });
    }
    this.loginEventCallbacks.add(forgottenPasswordCallback);
    window.addEventListener(
      "message",
      forgottenPasswordCallback
    );

    let forgottenPasswordFailureCallback = (event: MessageEvent) => {
      if (event.origin !== window.origin || event.data !== FORGOT_PASSWORD_FAILURE) {
        return
      };
      window.removeEventListener("message", forgottenPasswordFailureCallback);
      this.loginEventCallbacks.delete(forgottenPasswordFailureCallback);
      this.modalService.closeModal(SIGN_IN_MODAL_NAME);
      this.modalService.openModal(PASSWORD_RESET_RESULTS_MODAL_NAME, PasswordResetResultsComponent, {
        inputValues: {
          success: false
        },
        modal: true,
        dismissableMask: true,
        closable: true,
      });
    }
    this.loginEventCallbacks.add(forgottenPasswordFailureCallback);
    window.addEventListener(
      "message",
      forgottenPasswordFailureCallback
    );

    let goToLoginCallback = (event: MessageEvent) => {
      if (event.origin !== window.origin || event.data !== GO_TO_LOGIN) {
        return
      };
      window.removeEventListener("message", goToLoginCallback);
      this.loginEventCallbacks.delete(goToLoginCallback);
      this.modalService.closeModal(SIGN_IN_MODAL_NAME);
      this.openLoginModal(LOGIN_TYPE_TOPBAR, () => { }, auctionId);
    }
    this.loginEventCallbacks.add(goToLoginCallback);
    window.addEventListener(
      "message",
      goToLoginCallback
    );

    let goToUnauthorizedErrorCallback = (event: MessageEvent) => {
      if (event.origin !== window.origin || event.data !== ERROR_UNAUTHORIZED) {
        return;
      }
      window.removeEventListener("message", goToUnauthorizedErrorCallback);
      this.loginEventCallbacks.delete(goToUnauthorizedErrorCallback);
      this.modalService.closeModal(SIGN_IN_MODAL_NAME);
      this.modalService.openModal(
        OIDC_ERROR_UNAUTHORIZED_MODAL_NAME,
        OidcErrorUnauthorizedComponent,
        {
          modal: true,
          dismissableMask: true,
          closable: true,
        }
      );
    }
    this.loginEventCallbacks.add(goToUnauthorizedErrorCallback);
    window.addEventListener(
      "message",
      goToUnauthorizedErrorCallback
    );

    this.oidcSecurityService.authorize("", authOptions)
  }

  logout() {
    this.oidcSecurityService.logoffAndRevokeTokens('', { urlHandler(url) { }, }).subscribe(() => {
      console.log('Signed out');
    });
  }

  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$;
  }

  postRegisterAccount(accountData: IAccountData): Observable<string> {

    const url = `${environment.apiUrl}/v3/accounts`;

    const body = new HttpParams()
      .set('accountData', JSON.stringify(accountData));

    console.log(body);
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Accept', 'application/json');

    return this.http.post<string>(url, body.toString(), {
      headers,
      responseType: 'text' as 'json'
    }).pipe(
      tap(response => {
        console.log('response register account', response);
        this.registerResponseSignal.set(response);
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(() => ({
          error: error.error

        }));
      })
    );
  }

  async addCreditCard(card: ICreditCardRequest): Promise<Observable<string>> {
    const url = `${environment.apiUrl}/v3/cards`;

    let token: string;
    try {
      token = await this.reCaptchaV3Service.executeAsPromise(environment.siteKey, 'add_credit_card');
    } catch {
      return throwError(() => new Error('ReCAPTCHA verification failed. Please try again.'));
    }

    const body = new HttpParams()
      .set('creditCard', JSON.stringify(card))
      .set('g-recaptcha-response', token);

    return from(this.oidcSecurityService.checkAuth()).pipe(
      switchMap(authResult => {
        if (authResult.isAuthenticated) {
          let headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          });
          headers = headers.set('Authorization', `Bearer ${authResult.accessToken}`);
          return this.http.post<string>(url, body.toString(), {
            headers,
            responseType: 'text' as 'json'
          });
        } else {
          return throwError(() => new Error('unauthenticated user or expired token'));
        }
      }),
      tap(response => {
        // console.log('response addCreditCard', response);
        this.creditCardResponseSignal.set(response);
      }), //catch error json
      catchError((error: HttpErrorResponse) => {
        let errorMsg = 'Unknown error';

        if (typeof error.error === 'object' && error.error?.message) {
          errorMsg = error.error.message;

        } else if (typeof error.error === 'string') {
          try {
            const parsed = JSON.parse(error.error);
            errorMsg = parsed.message || error.error;
          } catch (e) {
            errorMsg = error.error;
          }
        }

        return throwError(() => new Error(errorMsg));
      })
    );
  }

  getCreditCard(): Observable<ICreditCardResponse> {

    const url = `${environment.apiUrl}/v3/cards`;

    return from(this.oidcSecurityService.checkAuth()).pipe(
      switchMap(authResult => {
        if (authResult.isAuthenticated) {
          // return from(this.oidcSecurityService.getAccessToken()).pipe(
          //   switchMap(token => {
          let headers = new HttpHeaders();
          headers = headers.set('Authorization', `Bearer ${authResult.accessToken}`);
          return this.httpService.get<ICreditCardResponse>(url, undefined, headers);
          // })
          // );
        } else {
          return this.httpService.get<ICreditCardResponse>(url);
        }
      }),
      tap(response => {
        // console.log('credit card response:', response);
        this.creditCardSignal.set(response)
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMsg = 'Unknown error';

        if (typeof error.error === 'object' && error.error?.message) {
          errorMsg = error.error.message;

        } else if (typeof error.error === 'string') {
          try {
            const parsed = JSON.parse(error.error);
            errorMsg = parsed.message || error.error;
          } catch (e) {
            errorMsg = error.error;
          }
        }

        return throwError(() => new Error(errorMsg));
      })
    )
  }

  public patchPassword(data: IPasswordRequest): Observable<any> {
    const url = `${environment.apiUrl}/v3/accounts/my-account/password`;
    const body = new HttpParams()
      .set('oldPassword', data.oldPassword)
      .set('password', data.password);

    return from(this.oidcSecurityService.checkAuth()).pipe(
      switchMap(authResult => {
        if (authResult.isAuthenticated) {
          let headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authResult.accessToken}`
          });

          return this.http.patch<string>(url, body, {
            headers,
            responseType: 'text' as 'json'
          });
        } else {
          return throwError(() => new Error('unauthenticated user or expired token'));
        }
      })
    );
  }

}



